// Trigger collector for `on_edge_traverse`. When a unit moves from
// `fromCell` to `toCell`, every wall (INTERSECTION_2 aura) on the
// crossed edge fires its `on_edge_traverse` entries with the moving
// unit as `watchTarget`.
//
// Walls live on edges, not cells, so this collector is separate from
// the `on_enter_location` pipeline. The crossingEdge helper rejects
// diagonal moves. Airborne flyers cutting a corner don't traverse any
// edge, so wall damage doesn't fire on diagonals.
//
// Pure. No GameBoard state. Caller dispatches each entry's effects
// through whatever runtime it uses (typically `dispatchSilentOp` with
// `watchTarget: enteringUnit`).

import { findWallsBetween } from '../../core/wallTraversal';
import { isCardSilenced } from '../../core/keywordQuery';
import { isDisabled } from '../scope';
import { TRIGGER } from '../schema';

/**
 * Collect every (wall, fullWall, entries) tuple that should fire its
 * `on_edge_traverse` trigger because `unit` just crossed the edge
 * between `fromCell` and `toCell`.
 *
 * @param {object} unit
 * @param {{ col, row }} fromCell
 * @param {{ col, row }} toCell
 * @param {Iterable<[any, { userData, position }]>} meshes
 * @param {(cardId: string) => object | null} getFullCard
 * @returns {{ card: object, fullCard: object, entries: object[] }[]}
 */
export function collectOnEdgeTraverseFirings(unit, fromCell, toCell, meshes, getFullCard) {
  if (!unit || !fromCell || !toCell) return [];
  const walls = findWallsBetween(fromCell, toCell, meshes);
  if (walls.length === 0) return [];
  const out = [];
  for (const wall of walls) {
    // A silenced/disabled wall fires no edge-traverse trigger. Its
    // damaging box text is offline.
    if (isCardSilenced(wall) || isDisabled(wall)) continue;
    const fullCard = getFullCard(wall.cardId);
    const entries = (fullCard?.automation || []).filter((e) => e.trigger === TRIGGER.ON_EDGE_TRAVERSE);
    if (entries.length === 0) continue;
    out.push({ card: wall, fullCard, entries });
  }
  return out;
}
